// TAS-20 phase 2b: forged step-advance rollback (pure decision logic).
//
// The step-advance realtime broadcast is applied OPTIMISTICALLY (snappy, ~50-100ms)
// but it's UNAUTHENTICATED — any player can emit one and everyone obeys. The
// authoritative source is the session_live_state row, which only the GM can write
// (via advance_session_v2). A LEGIT advance is confirmed by that row catching up
// within a short window; a FORGED one never is. This decides whether an optimistic
// (broadcast) step should be rolled back to the authoritative truth.
//
// Kept pure (no React/Supabase) so the correctness-critical guards are unit-tested.

/**
 * @param {object} p
 * @param {object|null} p.authRow        session_live_state row { run_id, version, step_index }
 * @param {string|null} p.localRunId     our last-seen run_id
 * @param {number} p.optimisticStep      the step the broadcast optimistically set
 * @param {number} p.currentStep         where we are right now (stepIndexRef)
 * @returns {number|null} the step to roll back to, or null to leave it alone
 */
export function shouldRollbackForgedStep({ authRow, localRunId, optimisticStep, currentStep }) {
  if (!authRow || typeof authRow.step_index !== 'number') return null;
  // A newer step already superseded this optimistic one — its own check owns it.
  if (currentStep !== optimisticStep) return null;
  // Different run = epoch reset — handled by the forward/reset reconcile, not here.
  if (authRow.run_id && localRunId && authRow.run_id !== localRunId) return null;
  // Authoritative has caught up to (or past) us → the advance was confirmed. Keep it.
  if (authRow.step_index >= currentStep) return null;
  // Still sitting on the optimistic step, authoritative is BEHIND, same run, past the
  // confirmation window → the broadcast was never backed by a real GM advance → forged.
  return authRow.step_index;
}

// Public messages may request a bounded refresh, but cannot supply room state.
export function createBroadcastReconciler(read, { now = Date.now, cooldownMs = 500 } = {}) {
  let pending = null;
  let lastRead = -Infinity;
  return () => {
    if (pending) return pending;
    if (now() - lastRead < cooldownMs) return Promise.resolve();
    lastRead = now();
    pending = Promise.resolve().then(() => read()).catch(() => {}).finally(() => { pending = null; });
    return pending;
  };
}
